import type { Issue } from './types';

/**
 * How far through a set of work is, as a percentage.
 *
 * ABANDONED WORK LEAVES THE DENOMINATOR
 *
 * Something marked won't-do is neither outstanding nor an achievement. Counting
 * it as outstanding means an epic with nothing left to do never reaches 100% —
 * "Battery & heat" read 88% with fourteen done, two abandoned and nothing at all
 * remaining, which is not a progress bar anybody can act on.
 *
 * This exists as one function because the screen had the rule twice: the
 * headline excluded abandoned work and the per-epic rows did not, so two numbers
 * on the same screen answered the same question differently. Four of nine epics
 * disagreed with their own heading.
 */
export function countedForProgress<T extends Pick<Issue, 'status'>>(issues: T[]): T[] {
  return issues.filter((i) => i.status !== 'wontdo');
}

export function percentDone(issues: Pick<Issue, 'status'>[]): number {
  const counted = countedForProgress(issues);
  if (counted.length === 0) return 0;
  const done = counted.filter((i) => i.status === 'done').length;
  return Math.round((done / counted.length) * 100);
}

/**
 * The line under an epic's name: how much is left, and how much of it is stuck.
 *
 * It read "5 blocked of 5 left" and "1 blocked of 1 left" — compressed to the
 * point of being a puzzle, and the two numbers being equal is the single most
 * useful thing the line can say. Worth spelling out rather than leaving the
 * reader to notice.
 */
export function epicLine(open: number, blocked: number): string {
  if (open === 0) return 'nothing left to do';
  if (blocked === 0) return `${open} left, none blocked`;
  if (blocked >= open) {
    return open === 1 ? '1 left, and it is blocked' : `all ${open} left are blocked`;
  }
  return `${open} left, ${blocked} blocked`;
}
